/**
 *
 *  Organizations/Show/Users/Select
 *
 */

import React, { useState, useMemo } from 'react'

// NPM Libraries
import InfiniteScroll from 'react-infinite-scroll-component'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'

// GraphQL
import useQuery from 'components/UseQuery'
import { gql } from '@apollo/client'

// Components
import Spinner from 'components/Spinner'

// Utils
import { withCurrentUser } from 'currentUserContext'
import { COLORS } from 'global-styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const LOAD_USERS = gql`
	query getUsersConnection(
		$number_of_users: Int
		$filters: [JSON]
		$user_role: Int
		$cursor: String
		$with_campaigns: Boolean
		$viewer_id: ID
	) {
		usersConnection(
			first: $number_of_users
			filters: $filters
			user_role: $user_role
			after: $cursor
			with_campaigns: $with_campaigns
			viewer_id: $viewer_id
		) {
			pageInfo {
				endCursor
				hasNextPage
			}
			edges {
				cursor
				node {
					id
					full_name
					email
					company_name
					profile_image
				}
			}
		}
	}
`

const UserList = styled.ul`
	margin: 0;
	padding: 0;

	li {
		padding: 10px 20px;
		border: 1px solid ${COLORS.grey_e};
		border-top: 0;

		display: flex;
		align-items: center;
		justify-content: space-between;

		cursor: pointer;

		p {
			margin: 0;
			line-height: 1.2rem;
		}

		&.selected {
			background-color: rgba(0, 146, 209, 0.1) !important;
		}

		&:hover {
			background-color: rgba(0, 146, 209, 0.2) !important;
		}

		&.loading {
			cursor: loading;
			opacity: 0.7;
		}
	}

	.infinite-scroll-component {
		&::-webkit-scrollbar {
			width: 4px;
		}

		&::-webkit-scrollbar-thumb {
			background-color: ${COLORS.grey};
			border-radius: 4px;
		}

		&::-webkit-scrollbar-track {
			background-color: ${COLORS.borderGrey};
		}
	}
`
const CustomInput = styled.div`
	position: relative;

	input {
		padding: 17px 10px 17px 35px !important;
		height: 35px;
		font-size: 0.98rem;
		width: 100%;
		border: 1px solid ${COLORS.grey_e};

		&:focus {
			outline: none !important;
			box-shadow: none !important;
		}
	}

	.search {
		position: absolute;
		top: 11px;
		left: 12px;
	}
`

function OrganizationUsersSelect({ onSelect, selected_users = [], current_user, loading }) {
	const [search_filters, setSearchFilters] = useState([{ id: 'all', value: '' }])

	const {
		data: {
			usersConnection: { pageInfo, edges },
		},
		refetch,
		fetchMore,
	} = useQuery(LOAD_USERS, {
		variables: { number_of_users: 20, filters: search_filters },
		default_data: {
			usersConnection: { pageInfo: {}, edges: [] },
		},
	})

	const getNext = () => {
		fetchMore({
			variables: {
				cursor: pageInfo.endCursor,
			},
			updateQuery: (previousResult, { fetchMoreResult }) => {
				const list = {
					usersConnection: {
						__typename: previousResult.usersConnection.__typename,
						pageInfo: fetchMoreResult.usersConnection.pageInfo,
						edges: [...previousResult.usersConnection.edges, ...fetchMoreResult.usersConnection.edges],
					},
				}

				return fetchMoreResult.usersConnection.edges.length ? list : previousResult
			},
		})
	}

	const current_user_selected = useMemo(
		() => selected_users.some((user) => user.id === current_user.id),
		[selected_users, current_user.id],
	)

	const formated_users_list = useMemo(() => {
		if (!Array.isArray(edges) || !Array.isArray(selected_users)) return []

		const selected_ids = new Set(selected_users.map((user) => user.id))

		return edges.map(({ node: user }) => ({
			...user,
			selected: selected_ids.has(user.id),
		}))
	}, [edges, selected_users])

	return (
		<div>
			<CustomInput>
				<input
					value={search_filters[0].value}
					className="form-control"
					placeholder="Search User"
					onChange={(e) => setSearchFilters([{ id: 'all', value: e.target.value }])}
				/>

				<FontAwesomeIcon icon="search" size="sm" className="search" />
			</CustomInput>

			<UserList>
				<InfiniteScroll
					refreshFunction={refetch}
					next={getNext}
					dataLength={edges.length}
					hasMore={pageInfo.hasNextPage}
					height={350}
					loader={
						<li className="list-group-item">
							<Spinner />
						</li>
					}
				>
					{current_user.full_name.match(search_filters[0].value) && (
						<li
							className={`${current_user_selected ? 'selected' : ''} ${loading ? 'loading' : ''}`}
							onClick={(event) => {
								if (loading) return
								event.preventDefault()
								onSelect(current_user)
							}}
						>
							<p>
								<strong>{current_user.full_name} (You)</strong>
								<br />
								<small className="text-muted">
									{[current_user.company_name, current_user.email].filter((x) => x).join(' - ')}
								</small>
							</p>

							{current_user_selected && <FontAwesomeIcon icon="check-circle" className="text-success" />}
						</li>
					)}
					{formated_users_list.map((user) => (
						<li
							key={user.id}
							onClick={() => onSelect(user)}
							className={`${user.selected ? 'selected' : ''} ${loading ? 'loading' : ''}`}
						>
							<p>
								<strong>{user.full_name}</strong>
								<br />
								<small className="text-muted">
									{[user.company_name, user.email].filter((x) => x).join(' - ')}
								</small>
							</p>

							{user.selected && <FontAwesomeIcon icon="check-circle" className="text-success" />}
						</li>
					))}
				</InfiniteScroll>
			</UserList>
		</div>
	)
}

OrganizationUsersSelect.propTypes = {
	onSelect: PropTypes.func.isRequired,
}

export default withCurrentUser(OrganizationUsersSelect)
